import { useEffect, useMemo, useState } from "react";
import { Routes, Route } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Path, Play, Stop, CalendarPlus, CheckCircle, Clock, ArrowRight } from "@phosphor-icons/react";

const NAV = [
  { key: "today", to: "/staff", label: "Today" },
  { key: "attendance", to: "/staff/attendance", label: "Attendance" },
  { key: "upcoming", to: "/staff/upcoming", label: "Upcoming" },
  { key: "history", to: "/staff/history", label: "History" },
];

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  deferred: "bg-rose-50 text-rose-700 border-rose-200",
};

function getGPS() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

function DeferDialog({ task, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/tasks/${task.id}/defer`, { new_date: date });
      toast.success(`Moved to ${date}`);
      setOpen(false);
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid={`defer-button-${task.id}`}>
          <CalendarPlus size={14} className="mr-1" /> Forward
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display">Forward task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm text-slate-600"><b>{task.title}</b></div>
          <div>
            <Label className="overline">New date</Label>
            <Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="mt-2" data-testid={`defer-date-input-${task.id}`} />
          </div>
          <DialogFooter><Button type="submit" disabled={saving} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid={`defer-submit-${task.id}`}>{saving ? "Saving…" : "Forward task"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EndDialog({ task, onDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const pos = await getGPS();
      const { data } = await api.post(`/tasks/${task.id}/end`, { lat: pos.lat, lng: pos.lng, notes });
      toast.success(`Completed · ${data.distance_km} km logged`);
      setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 h-11" data-testid={`end-task-${task.id}`}>
          <Stop size={16} weight="fill" className="mr-1" /> End task
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">Complete task</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-slate-600"><b>{task.title}</b></div>
          <div>
            <Label className="overline">Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2" data-testid={`end-notes-input-${task.id}`} />
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2"><MapPin size={14} /> On confirm, we capture your current GPS to calculate distance from where you started.</div>
          <Button disabled={saving} onClick={submit} className="w-full h-11 rounded-full bg-rose-600 hover:bg-rose-700" data-testid={`end-confirm-${task.id}`}>
            {saving ? "Capturing GPS…" : "Confirm & Complete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskItem({ task, onChange }) {
  const [starting, setStarting] = useState(false);
  const startTask = async () => {
    setStarting(true);
    try {
      const pos = await getGPS();
      await api.post(`/tasks/${task.id}/start`, { lat: pos.lat, lng: pos.lng });
      toast.success("Task started · GPS locked");
      onChange && onChange();
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally { setStarting(false); }
  };

  return (
    <Card className="border border-slate-200 shadow-none rounded-2xl overflow-hidden" data-testid={`task-card-${task.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={`${STATUS_STYLES[task.status]} border capitalize`}>{task.status.replace("_", " ")}</Badge>
              {task.priority === "high" && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">High</Badge>}
              <span className="text-xs text-slate-500 font-mono">{task.due_date}</span>
            </div>
            <div className="font-display font-bold text-xl tracking-tight">{task.title}</div>
            {task.description && <p className="text-slate-600 text-sm mt-1">{task.description}</p>}
            {task.status === "in_progress" && (
              <div className="mt-3 rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-xs text-indigo-800 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" />
                <span>Tracking · started at {new Date(task.started_at).toLocaleTimeString()}</span>
                <span className="ml-auto font-mono">{task.start_lat?.toFixed(4)}, {task.start_lng?.toFixed(4)}</span>
              </div>
            )}
            {task.status === "completed" && (
              <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800 flex items-center gap-2 flex-wrap">
                <CheckCircle size={16} weight="fill" />
                <span>Completed · {new Date(task.ended_at).toLocaleString()}</span>
                <span className="ml-auto font-mono font-bold">{task.distance_km?.toFixed(2)} km</span>
              </div>
            )}
          </div>
        </div>

        {task.status !== "completed" && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {task.status === "pending" || task.status === "deferred" ? (
              <Button
                onClick={startTask}
                disabled={starting}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-11 flex-1 sm:flex-none"
                data-testid={`start-task-${task.id}`}
              >
                <Play size={16} weight="fill" className="mr-1" /> {starting ? "Capturing GPS…" : "Start task"}
              </Button>
            ) : null}
            {task.status === "in_progress" && (<EndDialog task={task} onDone={onChange} />)}
            {task.status !== "in_progress" && (<DeferDialog task={task} onDone={onChange} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function useTasks(range) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      const today = new Date().toISOString().slice(0, 10);
      if (range === "today") { params.date_from = today; params.date_to = today; }
      if (range === "upcoming") { params.date_from = today; }
      if (range === "history") { params.date_to = today; }
      const { data } = await api.get("/tasks", { params });
      setTasks(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);
  return { tasks, loading, refresh: load };
}

function Section({ title, tasks, refresh, empty }) {
  return (
    <div className="space-y-3">
      <div className="overline">{title}</div>
      {tasks.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl p-6 text-center">{empty}</div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {tasks.map((t) => (<TaskItem key={t.id} task={t} onChange={refresh} />))}
        </div>
      )}
    </div>
  );
}

function Today() {
  const { tasks, refresh } = useTasks("today");
  const todayStr = new Date().toISOString().slice(0, 10);
  const pending = tasks.filter((t) => t.status === "pending" || t.status === "deferred");
  const active = tasks.filter((t) => t.status === "in_progress");
  const done = tasks.filter((t) => t.status === "completed");
  const totalKm = done.reduce((a, b) => a + (b.distance_km || 0), 0);

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Today · {todayStr}</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Your errands</h1>
        <p className="text-slate-500 mt-1">Start each errand to capture live GPS and log distance automatically.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="stat-card rounded-2xl p-4" data-testid="staff-stat-open">
          <div className="overline">Open</div>
          <div className="font-display font-black tracking-tighter text-3xl mt-2">{pending.length + active.length}</div>
        </div>
        <div className="stat-card rounded-2xl p-4" data-testid="staff-stat-done">
          <div className="overline">Done</div>
          <div className="font-display font-black tracking-tighter text-3xl mt-2 text-emerald-600">{done.length}</div>
        </div>
        <div className="stat-card rounded-2xl p-4" data-testid="staff-stat-km">
          <div className="overline">KM today</div>
          <div className="font-display font-black tracking-tighter text-3xl mt-2 text-indigo-600">{totalKm.toFixed(2)}</div>
        </div>
      </div>

      <Section title="In progress" tasks={active} refresh={refresh} empty="Nothing running right now." />
      <Section title="Pending & deferred" tasks={pending} refresh={refresh} empty="No open tasks — great job!" />
      <Section title="Completed today" tasks={done} refresh={refresh} empty="No completions yet." />
    </div>
  );
}

function Upcoming() {
  const { tasks, refresh } = useTasks("upcoming");
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = tasks.filter((t) => t.due_date > today && t.status !== "completed");
  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Coming up</div>
        <h1 className="font-display font-black tracking-tighter text-4xl">Upcoming tasks</h1>
      </div>
      <Section title="Scheduled ahead" tasks={upcoming} refresh={refresh} empty="Nothing scheduled ahead." />
    </div>
  );
}

function History() {
  const { tasks, refresh } = useTasks("history");
  const done = tasks.filter((t) => t.status === "completed");
  const totalKm = done.reduce((a, b) => a + (b.distance_km || 0), 0);
  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">History</div>
        <h1 className="font-display font-black tracking-tighter text-4xl">Completed errands</h1>
        <p className="text-slate-500 mt-1 font-mono text-sm">Total logged: <b>{totalKm.toFixed(2)} km</b> across {done.length} tasks.</p>
      </div>
      <Section title="Completed" tasks={done} refresh={refresh} empty="No history yet." />
    </div>
  );
}

export default function StaffDashboard() {
  return (
    <Layout nav={NAV}>
      <Routes>
        <Route index element={<Today />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="upcoming" element={<Upcoming />} />
        <Route path="history" element={<History />} />
      </Routes>
    </Layout>
  );
}

function Attendance() {
  const [today, setToday] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: t }, { data: list }] = await Promise.all([
      api.get("/attendance/today"),
      api.get("/attendance/list", { params: { month } }),
    ]);
    setToday(t);
    setRows(list);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const doPunch = async (kind) => {
    setBusy(true);
    try {
      let payload = { lat: null, lng: null };
      try { const p = await getGPS(); payload = { lat: p.lat, lng: p.lng }; } catch { /* GPS optional */ }
      await api.post(`/attendance/${kind}`, payload);
      toast.success(kind === "punch-in" ? "Punched in" : "Punched out");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
  const punchedIn = today && today.punch_in_at && !today.punch_out_at;
  const punchedOut = today && today.punch_out_at;
  const notPunched = !today || !today.punch_in_at;

  const monthHours = rows.reduce((a, r) => a + (r.hours_worked || 0), 0);
  const monthDays = rows.filter((r) => r.punch_out_at).length;

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Attendance</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Punch in / out</h1>
        <p className="text-slate-500 mt-1">Your time counts. We'll compute salary from your hours logged this month.</p>
      </div>

      <Card className="border-slate-200 shadow-none rounded-3xl overflow-hidden">
        <CardContent className="p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="overline">Punch in</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-2">{fmt(today?.punch_in_at)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="overline">Punch out</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-2">{fmt(today?.punch_out_at)}</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-slate-600">
            Today's hours: <b className="font-mono">{(today?.hours_worked ?? 0).toFixed(2)}</b>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              disabled={busy || !notPunched}
              onClick={() => doPunch("punch-in")}
              className="h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 flex-1 text-base"
              data-testid="punch-in-button"
            >
              <Play size={18} weight="fill" className="mr-2" /> {busy ? "Working…" : "Punch in"}
            </Button>
            <Button
              disabled={busy || !punchedIn}
              onClick={() => doPunch("punch-out")}
              className="h-14 rounded-full bg-rose-600 hover:bg-rose-700 flex-1 text-base"
              data-testid="punch-out-button"
            >
              <Stop size={18} weight="fill" className="mr-2" /> {busy ? "Working…" : "Punch out"}
            </Button>
          </div>
          {punchedOut && <div className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">Day closed. See you tomorrow.</div>}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <div className="stat-card rounded-2xl p-4" data-testid="att-month-days">
          <div className="overline">Days present · {month}</div>
          <div className="font-display font-black tracking-tighter text-3xl mt-2">{monthDays}</div>
        </div>
        <div className="stat-card rounded-2xl p-4" data-testid="att-month-hours">
          <div className="overline">Hours logged</div>
          <div className="font-display font-black tracking-tighter text-3xl mt-2 text-indigo-600">{monthHours.toFixed(2)}</div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="overline">Log</span>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="staff-att-month-input" />
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
            <div className="col-span-4">Date</div>
            <div className="col-span-3">In</div>
            <div className="col-span-3">Out</div>
            <div className="col-span-2">Hours</div>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100" data-testid={`staff-att-row-${r.id}`}>
              <div className="col-span-4 font-mono">{r.date}</div>
              <div className="col-span-3 font-mono">{fmt(r.punch_in_at)}</div>
              <div className="col-span-3 font-mono">{fmt(r.punch_out_at)}</div>
              <div className="col-span-2 font-mono font-bold">{(r.hours_worked || 0).toFixed(2)}</div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No records this month yet.</div>}
        </div>
      </div>
    </div>
  );
}
